/**
 * WorkflowContextTab — Shows where a task/instance sits in its parent workflow
 *
 * Only visible for: task, instance, approval, checkpoint primitives.
 * Shows workflow context fields as clickable navigation links.
 */

import {
  Stack,
  Text,
  Group,
  Badge,
  Center,
  Paper,
  SimpleGrid,
  Anchor,
  Button,
} from '@mantine/core';
import {
  IconCircleCheck,
  IconPlayerPlay,
  IconClock,
  IconCircleX,
  IconMap,
  IconExternalLink,
} from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';

// ============================================================================
// STATUS CONFIG
// ============================================================================

const STATUS_COLORS: Record<string, string> = {
  COMPLETED: 'green',
  ACTIVE: 'blue',
  IN_PROGRESS: 'blue',
  PENDING: 'yellow',
  FAILED: 'red',
  DRAFT: 'gray',
  ARCHIVED: 'gray',
  PAUSED: 'orange',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const STATUS_ICONS: Record<string, React.ComponentType<any>> = {
  COMPLETED: IconCircleCheck,
  ACTIVE: IconPlayerPlay,
  IN_PROGRESS: IconPlayerPlay,
  PENDING: IconClock,
  FAILED: IconCircleX,
};

// ============================================================================
// COMPONENT
// ============================================================================

interface WorkflowContextTabProps {
  entity: PrimitiveItem | null;
  primitiveType: string;
}

export function WorkflowContextTab({ entity, primitiveType }: Readonly<WorkflowContextTabProps>) {
  const workflowInstanceId = entity?.workflowInstanceId as string | undefined;
  const assignedActorId = (entity?.assignedActor as { id?: string; displayName?: string } | undefined)?.displayName
    ?? (entity?.assignedActor as { id?: string; displayName?: string } | undefined)?.id;
  const currentStatus = entity?.status as string | undefined;
  const parentId = entity?.parentId as string | undefined;
  const StatusIcon = currentStatus ? STATUS_ICONS[currentStatus] : undefined;

  if (!entity) {
    return (
      <Center py="xl">
        <Text c="dimmed" size="sm">Select an entity to view its workflow context.</Text>
      </Center>
    );
  }

  return (
    <Stack gap="md" py="md">
      <Text size="sm" fw={600}>Workflow Context</Text>

      {/* Entity position */}
      <Paper withBorder p="sm" radius="sm">
        <SimpleGrid cols={2} spacing="xs">
          <div>
            <Text size="xs" c="dimmed">Primitive Type</Text>
            <Badge size="sm" variant="light">{primitiveType}</Badge>
          </div>
          <div>
            <Text size="xs" c="dimmed">Current Status</Text>
            <Group gap={4}>
              {StatusIcon && <StatusIcon size={14} color={`var(--mantine-color-${STATUS_COLORS[currentStatus ?? ''] ?? 'gray'}-6)`} />}
              <Badge
                size="sm"
                variant="light"
                color={STATUS_COLORS[currentStatus ?? ''] ?? 'gray'}
              >
                {currentStatus ?? '—'}
              </Badge>
            </Group>
          </div>

          {workflowInstanceId && (
            <div>
              <Text size="xs" c="dimmed">Workflow Instance</Text>
              <Anchor
                component={Link}
                to={`/ferp-core/instances/${workflowInstanceId}`}
                size="xs"
                style={{ fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: 4 }}
              >
                {workflowInstanceId}
                <IconExternalLink size={10} />
              </Anchor>
            </div>
          )}

          {parentId && (
            <div>
              <Text size="xs" c="dimmed">Parent Task</Text>
              <Anchor
                component={Link}
                to={`/ferp-core/tasks/${parentId}`}
                size="xs"
                style={{ fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: 4 }}
              >
                {parentId}
                <IconExternalLink size={10} />
              </Anchor>
            </div>
          )}

          {assignedActorId && (
            <div>
              <Text size="xs" c="dimmed">Assigned Actor</Text>
              <Text size="sm" style={{ fontFamily: 'monospace' }}>{assignedActorId}</Text>
            </div>
          )}
        </SimpleGrid>
      </Paper>

      {/* Dependencies */}
      {entity.dependencies && Array.isArray(entity.dependencies) && (entity.dependencies as string[]).length > 0 && (
        <div>
          <Text size="xs" fw={600} mb="xs">Dependencies</Text>
          <Group gap="xs">
            {(entity.dependencies as string[]).map((dep: string) => (
              <Badge key={dep} variant="outline" size="sm">{dep}</Badge>
            ))}
          </Group>
        </div>
      )}

      {/* Sequence order */}
      {entity.sequenceOrder != null && (
        <div>
          <Text size="xs" fw={600} mb="xs">Sequence Position</Text>
          <Badge size="sm" variant="light" color="blue">
            Step {entity.sequenceOrder as number}
          </Badge>
        </div>
      )}

      {/* Navigation shortcuts */}
      <Stack gap="xs">
        {workflowInstanceId && (
          <Button
            component={Link}
            to={`/ferp-core/instances/${workflowInstanceId}`}
            variant="light"
            size="xs"
            leftSection={<IconMap size={14} />}
            rightSection={<IconExternalLink size={12} />}
          >
            View Workflow Instance
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
